'use client';

import _defineProperty from "/codebuild/output/src2066/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/codebuild/output/src2066/src/s3/01/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/codebuild/output/src2066/src/s3/01/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/codebuild/output/src2066/src/s3/01/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["classNames", "styles", "value", "onChange", "onChangeEnd", "size", "min", "max", "minRange", "maxRange", "step", "precision", "defaultValue", "name", "marks", "label", "labelTransitionProps", "labelAlwaysOn", "thumbFromLabel", "thumbToLabel", "showLabelOnHover", "thumbChildren", "disabled", "unstyled", "scale", "inverted", "className", "style", "vars", "hiddenInputProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { useUncontrolled, useMove } from '@mantine/hooks';
import { rem } from '../../../core/utils/units-converters/rem.mjs';
import { getSize, getRadius } from '../../../core/utils/get-size/get-size.mjs';
import { createVarsResolver } from '../../../core/styles-api/create-vars-resolver/create-vars-resolver.mjs';
import 'clsx';
import { getThemeColor } from '../../../core/MantineProvider/color-functions/get-theme-color/get-theme-color.mjs';
import '../../../core/MantineProvider/Mantine.context.mjs';
import '../../../core/MantineProvider/default-theme.mjs';
import '../../../core/MantineProvider/MantineProvider.mjs';
import '../../../core/MantineProvider/MantineThemeProvider/MantineThemeProvider.mjs';
import { useProps } from '../../../core/MantineProvider/use-props/use-props.mjs';
import '../../../core/MantineProvider/MantineCssVariables/MantineCssVariables.mjs';
import { useStyles } from '../../../core/styles-api/use-styles/use-styles.mjs';
import '../../../core/Box/Box.mjs';
import { factory } from '../../../core/factory/factory.mjs';
import { useDirection } from '../../../core/DirectionProvider/DirectionProvider.mjs';
import { SliderProvider } from '../Slider.context.mjs';
import { SliderRoot } from '../SliderRoot/SliderRoot.mjs';
import { Thumb } from '../Thumb/Thumb.mjs';
import { Track } from '../Track/Track.mjs';
import { getChangeValue } from '../utils/get-change-value/get-change-value.mjs';
import { getClientPosition } from '../utils/get-client-position/get-client-position.mjs';
import { getFloatingValue } from '../utils/get-floating-value/get-gloating-value.mjs';
import { getPosition } from '../utils/get-position/get-position.mjs';
import { getPrecision } from '../utils/get-precision/get-precision.mjs';
import classes from '../Slider.module.css.mjs';
var varsResolver = createVarsResolver(function (theme, _ref) {
  var size = _ref.size,
    color = _ref.color,
    thumbSize = _ref.thumbSize,
    radius = _ref.radius;
  return {
    root: {
      "--slider-size": getSize(size, "slider-size"),
      "--slider-color": color ? getThemeColor(color, theme) : void 0,
      "--slider-radius": radius === void 0 ? void 0 : getRadius(radius),
      "--slider-thumb-size": thumbSize !== void 0 ? rem(thumbSize) : "calc(var(--slider-size) * 2)"
    }
  };
});
var defaultProps = {
  min: 0,
  max: 100,
  minRange: 10,
  step: 1,
  marks: [],
  label: function label(f) {
    return f;
  },
  labelTransitionProps: {
    transition: "fade",
    duration: 0
  },
  labelAlwaysOn: false,
  showLabelOnHover: true,
  disabled: false,
  scale: function scale(v) {
    return v;
  }
};
var RangeSlider = factory(function (_props, ref) {
  var props = useProps("RangeSlider", defaultProps, _props);
  var classNames = props.classNames,
    styles = props.styles,
    value = props.value,
    onChange = props.onChange,
    onChangeEnd = props.onChangeEnd,
    size = props.size,
    min = props.min,
    max = props.max,
    minRange = props.minRange,
    maxRange = props.maxRange,
    step = props.step,
    _precision = props.precision,
    defaultValue = props.defaultValue,
    name = props.name,
    marks = props.marks,
    label = props.label,
    labelTransitionProps = props.labelTransitionProps,
    labelAlwaysOn = props.labelAlwaysOn,
    thumbFromLabel = props.thumbFromLabel,
    thumbToLabel = props.thumbToLabel,
    showLabelOnHover = props.showLabelOnHover,
    thumbChildren = props.thumbChildren,
    disabled = props.disabled,
    unstyled = props.unstyled,
    scale = props.scale,
    inverted = props.inverted,
    className = props.className,
    style = props.style,
    vars = props.vars,
    hiddenInputProps = props.hiddenInputProps,
    others = _objectWithoutProperties(props, _excluded);
  var getStyles = useStyles({
    name: "Slider",
    props: props,
    classes: classes,
    classNames: classNames,
    className: className,
    styles: styles,
    style: style,
    vars: vars,
    varsResolver: varsResolver,
    unstyled: unstyled
  });
  var _useDirection = useDirection(),
    dir = _useDirection.dir;
  var _useState = useState(-1),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hovered = _useState4[0],
    setHovered = _useState4[1];
  var _useUncontrolled = useUncontrolled({
      value: value,
      defaultValue: defaultValue,
      finalValue: [min, max],
      onChange: onChange
    }),
    _useUncontrolled2 = _slicedToArray(_useUncontrolled, 2),
    _value = _useUncontrolled2[0],
    setValue = _useUncontrolled2[1];
  var valueRef = useRef(_value);
  var thumbs = useRef([]);
  var thumbIndex = useRef(void 0);
  var positions = [getPosition({
    value: _value[0],
    min: min,
    max: max
  }), getPosition({
    value: _value[1],
    min: min,
    max: max
  })];
  var precision = _precision !== null && _precision !== void 0 ? _precision : getPrecision(step);
  var _setValue = function _setValue(val) {
    setValue(val);
    valueRef.current = val;
  };
  useEffect(function () {
    if (Array.isArray(value)) {
      valueRef.current = value;
    }
  }, Array.isArray(value) ? [value[0], value[1]] : [null, null]);
  var setRangedValue = function setRangedValue(val, index, triggerChangeEnd) {
    var clone = _toConsumableArray(valueRef.current);
    clone[index] = val;
    if (index === 0) {
      if (val > clone[1] - (minRange - 1e-9)) {
        clone[1] = Math.min(val + minRange, max);
      }
      if (val > (max - (minRange - 1e-9) || min)) {
        clone[index] = valueRef.current[index];
      }
      if (clone[1] - val > maxRange) {
        clone[1] = val + maxRange;
      }
    }
    if (index === 1) {
      if (val < clone[0] + minRange) {
        clone[0] = Math.max(val - minRange, min);
      }
      if (val < clone[0] + minRange) {
        clone[index] = valueRef.current[index];
      }
      if (val - clone[0] > maxRange) {
        clone[0] = val - maxRange;
      }
    }
    clone[0] = getFloatingValue(clone[0], precision);
    clone[1] = getFloatingValue(clone[1], precision);
    _setValue(clone);
    if (triggerChangeEnd) {
      onChangeEnd === null || onChangeEnd === void 0 || onChangeEnd(valueRef.current);
    }
  };
  var handleChange = function handleChange(val) {
    if (!disabled) {
      var nextValue = getChangeValue({
        value: val,
        min: min,
        max: max,
        step: step,
        precision: precision
      });
      setRangedValue(nextValue, thumbIndex.current, false);
    }
  };
  var _useMove = useMove(function (_ref2) {
      var x = _ref2.x;
      return handleChange(x);
    }, {
      onScrubEnd: function onScrubEnd() {
        return onChangeEnd === null || onChangeEnd === void 0 ? void 0 : onChangeEnd(valueRef.current);
      }
    }, dir),
    container = _useMove.ref,
    active = _useMove.active;
  function handleThumbMouseDown(index) {
    thumbIndex.current = index;
  }
  var handleTrackMouseDownCapture = function handleTrackMouseDownCapture(event) {
    container.current.focus();
    var rect = container.current.getBoundingClientRect();
    var changePosition = getClientPosition(event.nativeEvent);
    var changeValue = getChangeValue({
      value: changePosition - rect.left,
      max: max,
      min: min,
      step: step,
      containerWidth: rect.width
    });
    var nearestHandle = Math.abs(_value[0] - changeValue) > Math.abs(_value[1] - changeValue) ? 1 : 0;
    var _nearestHandle = dir === "ltr" ? nearestHandle : nearestHandle === 1 ? 0 : 1;
    thumbIndex.current = _nearestHandle;
  };
  var getFocusedThumbIndex = function getFocusedThumbIndex() {
    if (focused !== 1 && focused !== 0) {
      setFocused(0);
      return 0;
    }
    return focused;
  };
  var handleTrackKeydownCapture = function handleTrackKeydownCapture(event) {
    if (!disabled) {
      switch (event.key) {
        case "ArrowUp":
          {
            event.preventDefault();
            var focusedIndex = getFocusedThumbIndex();
            thumbs.current[focusedIndex].focus();
            setRangedValue(getFloatingValue(Math.min(Math.max(valueRef.current[focusedIndex] + step, min), max), precision), focusedIndex, true);
            break;
          }
        case "ArrowRight":
          {
            event.preventDefault();
            var _focusedIndex = getFocusedThumbIndex();
            thumbs.current[_focusedIndex].focus();
            setRangedValue(getFloatingValue(Math.min(Math.max(dir === "rtl" ? valueRef.current[_focusedIndex] - step : valueRef.current[_focusedIndex] + step, min), max), precision), _focusedIndex, true);
            break;
          }
        case "ArrowDown":
          {
            event.preventDefault();
            var _focusedIndex2 = getFocusedThumbIndex();
            thumbs.current[_focusedIndex2].focus();
            setRangedValue(getFloatingValue(Math.min(Math.max(valueRef.current[_focusedIndex2] - step, min), max), precision), _focusedIndex2, true);
            break;
          }
        case "ArrowLeft":
          {
            event.preventDefault();
            var _focusedIndex3 = getFocusedThumbIndex();
            thumbs.current[_focusedIndex3].focus();
            setRangedValue(getFloatingValue(Math.min(Math.max(dir === "rtl" ? valueRef.current[_focusedIndex3] + step : valueRef.current[_focusedIndex3] - step, min), max), precision), _focusedIndex3, true);
            break;
          }
      }
    }
  };
  var sharedThumbProps = {
    max: max,
    min: min,
    size: size,
    labelTransitionProps: labelTransitionProps,
    labelAlwaysOn: labelAlwaysOn,
    onBlur: function onBlur() {
      return setFocused(-1);
    }
  };
  var hasArrayThumbChildren = Array.isArray(thumbChildren);
  return /* @__PURE__ */jsx(SliderProvider, {
    value: {
      getStyles: getStyles
    },
    children: /* @__PURE__ */jsxs(SliderRoot, _objectSpread(_objectSpread({}, others), {}, {
      size: size,
      ref: ref,
      disabled: disabled,
      children: [/* @__PURE__ */jsxs(Track, {
        offset: positions[0],
        marksOffset: _value[0],
        filled: positions[1] - positions[0],
        marks: marks,
        inverted: inverted,
        min: min,
        max: max,
        value: _value[1],
        disabled: disabled,
        containerProps: {
          ref: container,
          onMouseEnter: showLabelOnHover ? function () {
            return setHovered(true);
          } : void 0,
          onMouseLeave: showLabelOnHover ? function () {
            return setHovered(false);
          } : void 0,
          onTouchStartCapture: handleTrackMouseDownCapture,
          onTouchEndCapture: function onTouchEndCapture() {
            thumbIndex.current = -1;
          },
          onMouseDownCapture: handleTrackMouseDownCapture,
          onMouseUpCapture: function onMouseUpCapture() {
            thumbIndex.current = -1;
          },
          onKeyDownCapture: handleTrackKeydownCapture
        },
        children: [/* @__PURE__ */jsx(Thumb, _objectSpread(_objectSpread({}, sharedThumbProps), {}, {
          value: scale(_value[0]),
          position: positions[0],
          dragging: active,
          label: typeof label === "function" ? label(getFloatingValue(scale(_value[0]), precision)) : label,
          ref: function ref(node) {
            thumbs.current[0] = node;
          },
          thumbLabel: thumbFromLabel,
          onMouseDown: function onMouseDown() {
            return handleThumbMouseDown(0);
          },
          onFocus: function onFocus() {
            return setFocused(0);
          },
          showLabelOnHover: showLabelOnHover,
          isHovered: hovered,
          disabled: disabled,
          children: hasArrayThumbChildren ? thumbChildren[0] : thumbChildren
        })), /* @__PURE__ */jsx(Thumb, _objectSpread(_objectSpread({}, sharedThumbProps), {}, {
          thumbLabel: thumbToLabel,
          value: scale(_value[1]),
          position: positions[1],
          dragging: active,
          label: typeof label === "function" ? label(getFloatingValue(scale(_value[1]), precision)) : label,
          ref: function ref(node) {
            thumbs.current[1] = node;
          },
          onMouseDown: function onMouseDown() {
            return handleThumbMouseDown(1);
          },
          onFocus: function onFocus() {
            return setFocused(1);
          },
          showLabelOnHover: showLabelOnHover,
          isHovered: hovered,
          disabled: disabled,
          children: hasArrayThumbChildren ? thumbChildren[1] : thumbChildren
        }))]
      }), /* @__PURE__ */jsx("input", _objectSpread({
        type: "hidden",
        name: "".concat(name, "_from"),
        value: _value[0]
      }, hiddenInputProps)), /* @__PURE__ */jsx("input", _objectSpread({
        type: "hidden",
        name: "".concat(name, "_to"),
        value: _value[1]
      }, hiddenInputProps))]
    }))
  });
});
RangeSlider.classes = classes;
RangeSlider.displayName = "@mantine/core/RangeSlider";
export { RangeSlider };